import {
  Component,
  input,
  OnInit, OnDestroy,
  DestroyRef,
  inject,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { LTSwitchComponent } from 'src/app/features/lt-shared-ui/lt-button/components/lt-switch/lt-switch.component';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { ClientInformation } from 'src/app/models/client-information';
import { Integration } from 'src/app/models/integration';
import { SearchTerm } from 'src/app/models/search-term';
import { User } from 'src/app/models/user';
import { ClientInformationService } from 'src/app/services/client-information.service';
import { ClientIntegrationService } from 'src/app/services/client-integration.service';
import { ToastService } from 'src/app/services/toast.service';
import { passUntilDestroyed } from 'src/app/utilities/rx-operators';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-edit-client',
  templateUrl: './edit-client.component.html',
  styleUrls: ['./edit-client.component.scss'],
  providers: [ClientIntegrationService],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    LTSwitchComponent,
  ],
})

export class EditClientComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  client = input<ClientInformation>();
  integrations = input<Integration[]>();
  clientInformation: ClientInformation;

  clients: ClientInformation[];

  componentDestroyed$ = new Subject<boolean>();
  activeUser: User;
  mergeClient: ClientInformation;
  mergeClientSelected = false;
  mergeClientIntegrations: Integration[];

  // clientName: string;
  // clientIntegrationAbbrName: string;
  // compensafeInternalName: string;
  // aliases: string;

  myControl = new UntypedFormControl();
  filteredOptions: Observable<ClientInformation[]>;

  finishedLoading = false;
  clientsLoaded = false;
  mergingClient = false;
  editingClient = false;
  mergeClientConfirm = false;
  viewingClient = true;
  addingClientInfo = false;


  aliasSearchTerm: SearchTerm;

  constructor(
    private clientIntegrationService: ClientIntegrationService,
    private route: ActivatedRoute,
    private toastService: ToastService,
    private router: Router,
    private user: User,
    private clientInformationService: ClientInformationService,
  ) {
    this.activeUser = user;
    this.aliasSearchTerm = new SearchTerm('', 'Search by Client Name or Alias');
  }

  ngOnInit(): void {
    // Only treat client information as existing if it has been saved (has an ID)
    this.clientInformation = this.client()?.clientInformationId ? new ClientInformation(this.client()) : null;
    this.finishedLoading = true;
    this.getAllClients();
  }

  getAllClients(): void {
    this.clientIntegrationService.getAllClients()
      .subscribe({
        next: (clients) => {
          this.clients = clients;

          this.filteredOptions = this.myControl.valueChanges.pipe(
            startWith(''),
            map(value => typeof value === 'string' ? value : value.name),
            map(name => name ? this._filter(name) : this.clients.slice()),
          );

          this.clientsLoaded = true;
        },
        error: (_error) => {
          this.toastService.toastCreate('Failed to get Client List', 'Danger', {
            autoClose: false,
          });
        },
      });
  }


  //Utility Methods Start
  checkAccess(role: string[]): boolean {
    return this.user.checkAccess(role);
  }

  setEditing() {
    this.viewingClient = false;
    this.editingClient = true;
  }

  setMerging() {
    this.viewingClient = false;
    this.mergingClient = true;
  }

  cancel() {
    this.mergingClient = false;
    this.editingClient = false;
    this.mergeClientSelected = false;
    this.mergeClientConfirm = false;
    this.viewingClient = true;
  }

  updateClient() {
    this.clientIntegrationService.updateClient(this.client())
      .subscribe({
        next: () => {
          this.toastService.toastCreate('Updated Client', 'Success', {
            autoClose: false,
          });
        },
        error: (_error) => {
          this.toastService.toastCreate('Failed to Update Client', 'Danger', {
            autoClose: false,
          });
        },
      });
  }

  displayClientName(client: ClientInformation): string {
    return client && client.clientDisplayName ? client.clientDisplayName : '';
  }

  private _filter(value: string): ClientInformation[] {
    const filterValue = value.toLowerCase();

    return this.clients.filter(option => option.clientDisplayName.toLowerCase().includes(filterValue));
  }

  ngOnDestroy() {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  public selectMergeClient(client: ClientInformation) {

    this.mergeClient = client;

    this.clientIntegrationService.getIntegrationsByClient(client.clientDisplayName)
      .subscribe({
        next: (integrations) => {
          this.mergeClientIntegrations = integrations;
        },
        error: (_error) => {
          this.toastService.toastCreate('Failed to Select Merge Clients', 'Danger', {
            autoClose: false,
          });
        },
      });

    this.mergeClientSelected = true;

  }

  public submit() {
    if (this.client() == this.mergeClient) {
      this.toastService.toastCreate('Invalid Input: The clients are the same.', 'Warning', {
        autoClose: false,
      });
      return;
    }

    for (const clientOneIntegration of this.integrations()) {
      for (const clientTwoIntegration of this.mergeClientIntegrations) {
        if (clientOneIntegration.name == clientTwoIntegration.name) {
          this.toastService.toastCreate('Invalid Input: The clients have a matching integration name: ' + clientOneIntegration.name + '.', 'Warning', {
            autoClose: false,
          });
          return;
        }
      }
    }

    this.mergingClient = false;
    this.mergeClientConfirm = true;
  }

  mergeClients() {
    this.clientIntegrationService.mergeClients(this.client(), this.mergeClient)
      .subscribe(() => {
        this.toastService.toastCreate('Clients merged.', 'Success', {
          autoClose: false,
          keepAfterRouteChange: true,
        });

        this.router.navigate(['/editConfig', this.mergeClient.clientDisplayName]);
      },
      );
  }

  addClientInformation() {
    this.addingClientInfo = true;
    const clientInformation: ClientInformation = {
      clientFullName: this.client().clientFullName,
      clientShortName: this.client().clientShortName ?? '',
      losTalkerClientId: this.client().losTalkerClientId,
      clientDisplayName: this.client().clientDisplayName,
      clientAbbreviatedName: this.client().clientAbbreviatedName,
      compensafeInternalName: this.client().compensafeInternalName,
      dataSourceTypeAgg: this.client().dataSourceTypeAgg,
    };

    this.clientInformationService.insertClientInformation(clientInformation)
      .pipe(passUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.clientInformation = clientInformation;
          this.addingClientInfo = false;
          this.toastService.toastCreate('Successfully populated client information for ' + clientInformation.clientDisplayName + '.', 'Success', {
            autoClose: false,
          });
        },
        error: (_error) => {
          this.addingClientInfo = false;
          this.toastService.toastCreate('Failed to populate client information for ' + clientInformation.clientDisplayName + '.', 'Danger', {
            autoClose: false,
          });
        },
      });
  }
}
